(() => {
  /* ── Config ── */
  const { MONEY_FORMAT = '', CURRENCY = '', PRODUCT_HANDLE = '' } = window;

  /* ── Shared styles ── */
  const CELL = 'border border-gray-300 px-3 py-2';
  const TABLE = 'w-full mt-2 border-collapse border border-gray-300';

  /* ── Helpers ── */
  const parseJson = (id) => {
    const el = document.getElementById(id);
    return el ? JSON.parse(el.textContent) : null;
  };

  const updateText = (selector, value) => {
    const el = document.querySelector(selector);
    if (el) el.textContent = value || '';
  };

  const formatMoney = (cents) =>
    Shopify.formatMoney
      ? Shopify.formatMoney(cents, MONEY_FORMAT)
      : (cents / 100).toLocaleString(undefined, { style: 'currency', currency: CURRENCY });

  /* ── Variant change handlers (collected, dispatched from one listener) ── */
  const variantHandlers = [];

  const onVariantChange = (handler) => variantHandlers.push(handler);

  /* ── Product detail updates ── */
  const initProductDetail = () => {
    if (!PRODUCT_HANDLE) return;

    onVariantChange(async (variantId) => {
      try {
        const res = await fetch(`${Shopify.routes.root}products/${PRODUCT_HANDLE}.js`);
        const product = await res.json();
        const variant = product.variants.find((v) => v.id == variantId);
        if (!variant) return;

        updateText('[data-product-sku]', variant.sku);

        if (variant.price) {
          updateText('[data-product-price-wholesale]', formatMoney(variant.price));
        }

        if (variant.compare_at_price) {
          const price = formatMoney(variant.compare_at_price);
          updateText('[data-product-price-msrp]', price);
          updateText('[data-product-price-retail]', price);
        }

        history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      } catch (err) {
        console.error('Error updating variant:', err);
      }
    });
  };

  /* ── Components table ── */
  const componentCache = {};
  const productDataCache = {};

  // Expose productDataCache for pdp-drawer.js
  window.__pdpDrawer = { productDataCache };

  const lookupSku = async (sku) => {
    if (componentCache[sku]) return componentCache[sku];

    try {
      const searchRes = await fetch(
        `${Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=5&resources[options][fields]=variants.sku`
      );
      const searchData = await searchRes.json();
      const products = searchData.resources?.results?.products || [];

      for (const p of products) {
        const prodRes = await fetch(`${Shopify.routes.root}products/${p.handle}.js`);
        const prodData = await prodRes.json();
        const variant = prodData.variants.find((v) => v.sku === sku);
        if (variant) {
          const url = `${Shopify.routes.root}products/${prodData.handle}`;
          productDataCache[url] = prodData;
          const result = {
            title: prodData.title,
            url,
            sku: variant.sku,
            available: variant.available,
            price: variant.price,
          };
          componentCache[sku] = result;
          return result;
        }
      }
    } catch (err) {
      console.error(`Error looking up component SKU ${sku}:`, err);
    }

    componentCache[sku] = null;
    return null;
  };

  const initComponentsTable = (sectionId) => {
    const container = document.getElementById(`ComponentsTable-${sectionId}`);
    if (!container) return;
    const variantsMap = parseJson(`VariantComponentsJson-${sectionId}`);
    const tbody = container.querySelector('.components-table tbody');
    if (!tbody || !variantsMap) return;

    const stockHtml = (data) => {
      if (!data) return '<span class="text-gray-500">Not Found</span>';
      return data.available
        ? '<span class="text-green-600 font-semibold">In Stock</span>'
        : '<span class="text-red-600 font-semibold">Out of Stock</span>';
    };

    const skuHtml = (sku, data) => {
      if (data?.url) return `<a href="${data.url}" class="underline text-blue-600 hover:text-blue-800">${sku}</a>`;
      return sku;
    };

    const updateRow = (row) => {
      const sku = row.dataset.sku;
      const data = componentCache[sku];
      const stockCell = row.querySelector('.stock-status');
      const skuCell = row.querySelector('.sku-cell');
      if (stockCell) stockCell.innerHTML = stockHtml(data);
      if (skuCell) skuCell.innerHTML = skuHtml(sku, data);
    };

    const render = async (metafield) => {
      if (!metafield) {
        tbody.innerHTML = `<tr><td colspan="3" class="${CELL} text-gray-500">No components available for this variant.</td></tr>`;
        return;
      }

      const components = metafield.split(',').map((pair) => {
        const [sku, qty] = pair.split('=');
        return { sku: sku.trim(), qty: qty.trim() };
      });

      // Render rows immediately with loading placeholders
      tbody.innerHTML = components.map(({ sku, qty }) => `
        <tr class="component-row" data-sku="${sku}">
          <td class="${CELL} sku-cell">${sku}</td>
          <td class="${CELL}">${qty}</td>
          <td class="${CELL} stock-status"><span class="text-gray-400">Loading...</span></td>
        </tr>`).join('');

      // Look up all SKUs in parallel, then fill in stock status and link SKUs
      await Promise.all(components.map(({ sku }) => lookupSku(sku)));
      tbody.querySelectorAll('.component-row').forEach(updateRow);
    };

    // Resolve stock status for server-rendered rows
    const serverRows = tbody.querySelectorAll('.component-row');
    if (serverRows.length) {
      const skus = [...serverRows].map((r) => r.dataset.sku);
      Promise.all(skus.map(lookupSku)).then(() => serverRows.forEach(updateRow));
    }

    onVariantChange((variantId) => render(variantsMap[variantId]));
  };

  /* ── Specs rendering ── */
  const renderSpecsGroup = (group) => {
    const rows = group.rows.filter((r) => r.value !== '' && r.value != null && !(Array.isArray(r.value) && !r.value.length));
    if (!rows.length) return '';

    const rowsHtml = rows.map(({ label, value, type }) => {
      const cell = type === 'list'
        ? `<ul class="list-disc list-inside space-y-0.5">${value.map((i) => `<li>${i}</li>`).join('')}</ul>`
        : String(value);
      return `<tr>
        <td class="${CELL} font-medium align-top">${label}</td>
        <td class="${CELL}">${cell}</td>
      </tr>`;
    }).join('');

    return `<div class="text-sm text-gray-600 mb-6">
      <strong>${group.label}</strong>
      <table class="${TABLE} table-fixed">
        <colgroup><col class="w-2/5"><col class="w-3/5"></colgroup>
        <tbody>${rowsHtml}</tbody>
      </table>
    </div>`;
  };

  /* ── Specs table ── */
  const initSpecsTable = (sectionId) => {
    const container = document.getElementById(`SpecsTable-${sectionId}`);
    const specsMap = parseJson(`VariantSpecsJson-${sectionId}`);
    if (!container || !specsMap) return;

    onVariantChange((variantId) => {
      const groups = specsMap[variantId];
      if (!groups || !groups.length) {
        container.innerHTML = '';
        return;
      }
      container.innerHTML = groups.map(renderSpecsGroup).join('');
    });
  };

  /* ── Quick-order price toggle ── */
  const initPriceToggle = () => {
    const toggle = document.getElementById('price-toggle');
    if (!toggle) return;

    const cells = document.querySelectorAll('.price-cell');
    const header = document.getElementById('price-header');

    toggle.addEventListener('change', () => {
      const showWholesale = toggle.checked;
      cells.forEach((c) => (c.textContent = showWholesale ? c.dataset.price : c.dataset.comparePrice));
      if (header) header.textContent = showWholesale ? 'Wholesale' : 'Retail';
    });
  };

  /* ── Bootstrap ── */
  const init = () => {
    initProductDetail();

    document.querySelectorAll('[id^="ComponentsTable-"]').forEach((el) =>
      initComponentsTable(el.id.replace('ComponentsTable-', ''))
    );

    document.querySelectorAll('[id^="SpecsTable-"]').forEach((el) =>
      initSpecsTable(el.id.replace('SpecsTable-', ''))
    );

    initPriceToggle();

    // Single delegated listener for all variant changes
    document.addEventListener('change', (e) => {
      if (e.target?.name !== 'id') return;
      variantHandlers.forEach((fn) => fn(e.target.value));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
